/**
 * RulesSidebar component - slide-out panel for creating/editing rules.
 */

import {
  useCreateRule,
  useSearchCarriers,
  useSearchCustomers,
  useUpdateRule,
} from "@/hooks/useRules";
import {
  AGENT_TYPES,
  AGENT_TYPE_LABELS,
  AgentType,
  CarrierOption,
  CustomerOption,
  DOCUMENT_TYPES,
  ProcessingRule,
  RuleCreateData,
  SOURCE_TYPES,
} from "@/types/rules";
import { Autocomplete, Button, CircularProgress, Switch, TextField } from "@mui/material";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useState } from "react";

interface RulesSidebarProps {
  open: boolean;
  onClose: () => void;
  mode: "create" | "edit";
  rule?: ProcessingRule;
}

export function RulesSidebar({ open, onClose, mode, rule }: RulesSidebarProps) {
  const { enqueueSnackbar } = useSnackbar();
  const createRuleMutation = useCreateRule();
  const updateRuleMutation = useUpdateRule();

  // Form state
  const [agentType, setAgentType] = useState<AgentType | null>(null);
  const [ruleText, setRuleText] = useState("");
  const [rulePriority, setRulePriority] = useState(100);
  const [isActive, setIsActive] = useState(true);
  const [documentType, setDocumentType] = useState<string | null>(null);
  const [sourceType, setSourceType] = useState<string | null>(null);
  const [carrierSearch, setCarrierSearch] = useState("");
  const [selectedCarrier, setSelectedCarrier] = useState<CarrierOption | null>(null);
  const [customerSearch, setCustomerSearch] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerOption | null>(null);

  // Search queries with debouncing
  const { data: carriersData, isLoading: isLoadingCarriers } = useSearchCarriers(
    carrierSearch,
    carrierSearch.length >= 2
  );
  const { data: customersData, isLoading: isLoadingCustomers } = useSearchCustomers(
    customerSearch,
    customerSearch.length >= 2
  );

  const resetForm = useCallback(() => {
    setAgentType(null);
    setRuleText("");
    setRulePriority(100);
    setIsActive(true);
    setDocumentType(null);
    setSourceType(null);
    setCarrierSearch("");
    setSelectedCarrier(null);
    setCustomerSearch("");
    setSelectedCustomer(null);
  }, []);

  // Initialize form when editing
  useEffect(() => {
    if (open) {
      if (mode === "edit" && rule) {
        setAgentType(rule.agent_type as AgentType);
        setRuleText(rule.rule_text);
        setRulePriority(rule.rule_priority);
        setIsActive(rule.is_active);
        setDocumentType(rule.document_type);
        setSourceType(rule.source_type);
        // Note: carrier and customer would need to be fetched if we want to show full details
      } else if (mode === "create") {
        resetForm();
      }
    }
  }, [mode, rule, open, resetForm]);

  // Cleanup when sidebar closes
  useEffect(() => {
    if (!open) {
      // Small delay to allow closing animation
      const timer = setTimeout(() => {
        resetForm();
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [open, resetForm]);

  const validateForm = useCallback((): boolean => {
    // Required fields
    if (!agentType) {
      enqueueSnackbar("Agent type is required", { variant: "error" });
      return false;
    }

    if (!ruleText.trim()) {
      enqueueSnackbar("Rule text is required", { variant: "error" });
      return false;
    }

    // Rule text length validation
    if (ruleText.trim().length < 10) {
      enqueueSnackbar("Rule text must be at least 10 characters", { variant: "error" });
      return false;
    }

    if (ruleText.length > 5000) {
      enqueueSnackbar("Rule text cannot exceed 5000 characters", { variant: "error" });
      return false;
    }

    // Priority validation
    if (!Number.isInteger(rulePriority)) {
      enqueueSnackbar("Priority must be a whole number", { variant: "error" });
      return false;
    }

    if (rulePriority <= 0) {
      enqueueSnackbar("Priority must be greater than 0", { variant: "error" });
      return false;
    }

    if (rulePriority > 10000) {
      enqueueSnackbar("Priority cannot exceed 10000", { variant: "error" });
      return false;
    }

    return true;
  }, [agentType, ruleText, rulePriority, enqueueSnackbar]);

  const handleSubmit = useCallback(async () => {
    // Validate form
    if (!validateForm()) {
      return;
    }

    // validateForm ensures agentType is not null
    const data: RuleCreateData = {
      agent_type: agentType as AgentType,
      rule_text: ruleText.trim(),
      rule_priority: rulePriority,
      is_active: isActive,
      document_type: documentType || null,
      source_type: sourceType || null,
      carrier_code: selectedCarrier?.ref_carrier_code || null,
      customer_ref_code: selectedCustomer?.ref_client_code || null,
    };

    try {
      if (mode === "create") {
        await createRuleMutation.mutateAsync(data);
        enqueueSnackbar("Rule created successfully", { variant: "success" });
      } else if (mode === "edit" && rule) {
        await updateRuleMutation.mutateAsync({
          ruleId: rule.id,
          data: {
            rule_text: data.rule_text,
            rule_priority: data.rule_priority,
            is_active: data.is_active,
            document_type: data.document_type,
            source_type: data.source_type,
            carrier_code: data.carrier_code,
            customer_ref_code: data.customer_ref_code,
          },
        });
        enqueueSnackbar("Rule updated successfully", { variant: "success" });
      }
      onClose();
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      enqueueSnackbar(`Failed to ${mode} rule: ${message}`, { variant: "error" });
    }
  }, [
    validateForm,
    agentType,
    ruleText,
    rulePriority,
    isActive,
    documentType,
    sourceType,
    selectedCarrier,
    selectedCustomer,
    mode,
    rule,
    createRuleMutation,
    updateRuleMutation,
    enqueueSnackbar,
    onClose,
  ]);

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  // Handle Escape key to close sidebar
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && open) {
        handleCancel();
      }
    };

    if (open) {
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }
  }, [open, handleCancel]);

  if (!open) return null;

  const isSubmitting = createRuleMutation.isPending || updateRuleMutation.isPending;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-sm transition-opacity"
        onClick={handleCancel}
      />

      {/* Sidebar Panel */}
      <div
        className="fixed inset-y-0 right-0 z-50 flex w-full max-w-lg flex-col bg-white shadow-2xl transition-transform dark:bg-[#111722]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sidebar-title"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-5 dark:border-slate-800">
          <div>
            <h2
              id="sidebar-title"
              className="text-xl font-bold tracking-tight text-slate-900 dark:text-white"
            >
              {mode === "create" ? "Create New Processing Rule" : "Edit Processing Rule"}
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Define agent logic and specific processing scope
            </p>
          </div>
          <button
            onClick={handleCancel}
            className="rounded-full p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800"
            aria-label="Close sidebar"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        {/* Form Body (Scrollable) */}
        <div className="custom-scrollbar flex-1 space-y-8 overflow-y-auto px-6 py-6">
          {/* Basic Configuration */}
          <section className="space-y-5">
            {/* Agent Type */}
            <div className="space-y-2">
              <label
                id="agent-type-label"
                className="block text-sm font-semibold text-slate-700 dark:text-slate-300"
              >
                Agent Type <span className="text-red-500">*</span>
              </label>
              <Autocomplete
                value={agentType}
                onChange={(_, newValue) => setAgentType(newValue)}
                options={AGENT_TYPES}
                getOptionLabel={(option) => AGENT_TYPE_LABELS[option]}
                disabled={mode === "edit"}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select agent type..."
                    size="small"
                    className="bg-slate-50 dark:bg-slate-800"
                    inputProps={{
                      ...params.inputProps,
                      "aria-labelledby": "agent-type-label",
                    }}
                  />
                )}
              />
            </div>

            {/* Rule Text */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300">
                  Rule Text (Prompt Instructions) <span className="text-red-500">*</span>
                </label>
                <span className="rounded bg-primary/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary">
                  AI Logic
                </span>
              </div>
              <textarea
                value={ruleText}
                onChange={(e) => setRuleText(e.target.value)}
                placeholder="Define the system prompt or injection rules for the agent here..."
                rows={6}
                maxLength={5000}
                className="w-full rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 font-mono text-sm outline-none transition-all focus:border-primary focus:ring-2 focus:ring-primary dark:border-slate-700 dark:bg-slate-800"
                aria-label="Rule text"
                aria-required="true"
              />
              <div className="flex justify-between text-xs text-slate-500 dark:text-slate-400">
                <span>
                  {ruleText.length < 10 && ruleText.length > 0 && (
                    <span className="text-orange-600 dark:text-orange-400">
                      Minimum 10 characters required
                    </span>
                  )}
                </span>
                <span
                  className={ruleText.length > 4500 ? "text-orange-600 dark:text-orange-400" : ""}
                >
                  {ruleText.length} / 5000
                </span>
              </div>
            </div>

            {/* Priority and Status */}
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1 space-y-2">
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300">
                  Rule Priority
                </label>
                <input
                  type="number"
                  value={rulePriority}
                  onChange={(e) => setRulePriority(parseInt(e.target.value, 10) || 0)}
                  min={1}
                  max={10000}
                  className="w-full rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none transition-all focus:border-primary focus:ring-2 focus:ring-primary dark:border-slate-700 dark:bg-slate-800"
                  aria-label="Rule priority"
                  aria-describedby="priority-hint"
                />
                <p id="priority-hint" className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  Lower priority (1-10) = applied first, higher priority (50+) = can override
                </p>
              </div>
              <div className="flex-1 space-y-2">
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300">
                  Status
                </label>
                <div className="flex h-[50px] items-center">
                  <label className="relative inline-flex cursor-pointer items-center">
                    <Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
                    <span className="ml-3 text-sm font-medium text-slate-700 dark:text-slate-300">
                      Active
                    </span>
                  </label>
                </div>
              </div>
            </div>
          </section>

          {/* Scope Filters */}
          <section className="space-y-5">
            <div className="border-t border-slate-200 pt-5 dark:border-slate-800">
              <h3 className="mb-1 text-sm font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Scope Filters (Optional)
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Leave empty for global rules that apply to all documents
              </p>
            </div>

            {/* Document Type */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300">
                Document Type
              </label>
              <Autocomplete
                value={documentType}
                onChange={(_, newValue) => setDocumentType(newValue)}
                options={[...DOCUMENT_TYPES, "Custom"]}
                freeSolo
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="BOL, DR, INVOICE, AT..."
                    size="small"
                    className="bg-slate-50 dark:bg-slate-800"
                  />
                )}
              />
            </div>

            {/* Source Type */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300">
                Source Type
              </label>
              <Autocomplete
                value={sourceType}
                onChange={(_, newValue) => setSourceType(newValue)}
                options={[...SOURCE_TYPES]}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="maven, agent_ftp, email..."
                    size="small"
                    className="bg-slate-50 dark:bg-slate-800"
                  />
                )}
              />
            </div>

            {/* Carrier */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300">
                Carrier
              </label>
              <Autocomplete
                value={selectedCarrier}
                onChange={(_, newValue) => setSelectedCarrier(newValue)}
                inputValue={carrierSearch}
                onInputChange={(_, newValue) => setCarrierSearch(newValue)}
                options={carriersData?.carriers || []}
                getOptionLabel={(option) =>
                  `${option.name || "Unknown"} (${option.ref_carrier_code || "N/A"})`
                }
                loading={isLoadingCarriers}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search carriers..."
                    size="small"
                    className="bg-slate-50 dark:bg-slate-800"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isLoadingCarriers && <CircularProgress size={20} />}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </div>

            {/* Customer */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300">
                Customer
              </label>
              <Autocomplete
                value={selectedCustomer}
                onChange={(_, newValue) => setSelectedCustomer(newValue)}
                inputValue={customerSearch}
                onInputChange={(_, newValue) => setCustomerSearch(newValue)}
                options={customersData?.customers || []}
                getOptionLabel={(option) =>
                  `${option.name || "Unknown"} — ${option.ref_client_code || "N/A"} — ${option.address || ""}, ${option.city || ""}, ${option.state || ""} ${option.zip_code || ""}`
                }
                loading={isLoadingCustomers}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search customers..."
                    size="small"
                    className="bg-slate-50 dark:bg-slate-800"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isLoadingCustomers && <CircularProgress size={20} />}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 border-t border-slate-200 px-6 py-4 dark:border-slate-800">
          <Button onClick={handleCancel} variant="outlined" disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isSubmitting}
            startIcon={isSubmitting && <CircularProgress size={16} />}
          >
            {mode === "create" ? "Create Rule" : "Save Changes"}
          </Button>
        </div>
      </div>
    </>
  );
}
